import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  DownOutlined,
  UpOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Input, message } from "antd";
import axios from "axios";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import StyledTooltip from "src/components/reusable/StyledTooltip/StyledTooltip";
import {
  fetchNotificationSettings,
  fetchNotificationOptions,
} from "src/services/MyAccountAPIs";
import {
  updateNotificationChannelMasters,
  updatePaidMediaRule,
  createPaidMediaRule,
  deletePaidMediaRule,
  updateSocialRule,
  createSocialRule,
  deleteSocialRule,
  updateWebAnalyticsRule,
  createWebAnalyticsRule,
  deleteWebAnalyticsRule,
  updateSalesRule,
  createSalesRule,
  deleteSalesRule,
  fetchPaidMediaPlatforms,
  fetchPaidMediaCampaigns,
  fetchSocialPlatforms,
  fetchWebAnalyticsCities,
  fetchSalesCities,
  fetchSalesProducts,
  type CitiesResponse,
} from "src/services/NotificationAnnouncementsAPIs";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useLanguage } from "src/hooks/useLanguage";
import { getApiLangFromI18n } from "src/utils/apiLang";
import type {
  NotificationSection,
  NotificationSettingsItem,
  NotificationKind,
  NotificationOptionsResponse,
  NotificationSystem,
  NotificationChannelMasters,
  PaidMediaCampaign,
  PaidMediaRule,
  SocialRule,
  WebAnalyticsRule,
  SalesRule,
  SalesProduct,
  NotificationOption,
  SocialPlatformData,
} from "src/layouts/MyAccount/Notifications/NotificationInterfaces";
import { DefinitionTooltip } from "src/components/common";
import { getNotificationMetricHelp } from "./notificationMetricHelp";

type AnyRule = PaidMediaRule | SocialRule | WebAnalyticsRule | SalesRule;

interface ConfigureFormState {
  period: string;
  comparison_period: string;
  operator: string;
  threshold_value: string;
  threshold_value_upper: string;
  email: boolean;
  push: boolean;
  in_app: boolean;
  platforms: string[];
  campaigns: PaidMediaCampaign[];
  threshold_type: string;
  social_platforms: string[];
  targets: { platform: string; page_id: string; platform_label: string; page_name?: string; account_name?: string }[];
  cities: string[];
  products: SalesProduct[];
}

const DEFAULT_FORM: ConfigureFormState = {
  period: "daily",
  comparison_period: "wow",
  operator: "gt",
  threshold_value: "",
  threshold_value_upper: "",
  email: false,
  push: false,
  in_app: false,
  platforms: [],
  campaigns: [],
  threshold_type: "absolute",
  social_platforms: [],
  targets: [],
  cities: [],
  products: [],
};

const FALLBACK_SECTION_ICON = "/Icons/notif-web-analytics.svg";

const SECTION_ICONS: Record<string, string> = {
  sales: "/Icons/notif-sales.svg",
  paid_media: "/Icons/notif-insights.svg",
  social: "/Icons/notif-social.svg",
  web_analytics: "/Icons/notif-web-analytics.svg",
};

const OP_SYMBOL: Record<string, string> = {
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
};

const PLATFORM_LABELS: Record<string, string> = {
  meta_ads: "Meta",
  google_ads: "Google",
};

const PAID_MEDIA_METRIC_PLATFORM_RESTRICTIONS: Record<string, string[]> = {
  cost_per_purchase: ["meta_ads"],
  cost_per_acquisition: ["google_ads"],
  paid_view: ["meta_ads"],
};

const NEGATIVE_THRESHOLD_ALLOWED_KEYS: Partial<Record<NotificationSystem, string[]>> = {
  sales: ["revenue", "revenue_growth"],
  social: ["follower_growth"],
};

const isNegativeThresholdAllowed = (system: NotificationSystem, key: string): boolean =>
  NEGATIVE_THRESHOLD_ALLOWED_KEYS[system]?.includes(key) ?? false;

const CHANNEL_TABLE_LABEL_KEY: Record<"email" | "push" | "in_app", string> = {
  email: "email",
  push: "push",
  in_app: "inApp",
};

const UNIT_SYMBOLS: Record<string, string> = {
  percentage: "%",
  seconds: "s",
};

const getErrorDetail = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
  }
  return fallback;
};

const thresholdText = (
  r: {
    operator: string | null;
    threshold_value: number | null;
    threshold_value_upper?: number | null;
  },
  sym: string,
  unit = ""
): string => {
  if (r.operator === "between" && r.threshold_value_upper !== null) {
    return `between ${r.threshold_value}${unit} and ${r.threshold_value_upper}${unit}`;
  }
  return `${sym} ${r.threshold_value}${unit}`;
};

const formatCityPart = (cities: string[]): string =>
  ` (${cities.slice(0, 2).join(", ")}${cities.length > 2 ? "…" : ""})`;

const getPaidMediaChipLabel = (
  item: NotificationSettingsItem,
  r: PaidMediaRule,
  sym: string
): string => {
  const platStr = r.platforms.map(p => PLATFORM_LABELS[p] ?? p).join(", ");
  if (item.kind === "summary") return `${platStr} · ${r.period}`;
  return `${platStr} ${thresholdText(r, sym)}`;
};

const capitalizePlatform = (p: string): string =>
  p ? p.charAt(0).toUpperCase() + p.slice(1) : p;

const getSocialPlatformStr = (r: SocialRule): string => {
  const labelByPlatform = new Map(
    (r.targets ?? []).map(tgt => [tgt.platform, tgt.platform_label] as const)
  );
  return (r.platforms ?? [])
    .map(p => labelByPlatform.get(p) ?? capitalizePlatform(p))
    .join(", ");
};

const getSocialChipLabel = (
  item: NotificationSettingsItem,
  r: SocialRule,
  sym: string
): string => {
  const platStr = getSocialPlatformStr(r);
  if (item.kind === "summary") {
    return platStr ? `${platStr} · ${r.period}` : r.period;
  }
  const unit = item.unit === "percentage" ? "%" : "";
  const cond = thresholdText(r, sym, unit);
  return platStr ? `${platStr} ${cond}` : cond;
};

const getWebAnalyticsChipLabel = (
  item: NotificationSettingsItem,
  r: WebAnalyticsRule,
  sym: string
): string => {
  if (item.kind === "pages") return r.period;
  const unit = UNIT_SYMBOLS[r.unit] ?? "";
  const cityPart = r.cities?.length ? formatCityPart(r.cities) : "";
  return `${thresholdText(r, sym, unit)}${cityPart}`;
};

const formatScopePart = (r: SalesRule): string => {
  const names = [
    ...(r.cities ?? []),
    ...((r.products ?? []).map(p => p.product_name)),
  ];
  return names.length ? formatCityPart(names) : "";
};

const getSalesChipLabel = (
  item: NotificationSettingsItem,
  r: SalesRule,
  sym: string
): string => {
  const unit = UNIT_SYMBOLS[r.unit] ?? "";
  const scope = formatScopePart(r);
  if (item.kind === "growth") {
    return `${thresholdText(r, sym, unit)} · ${r.comparison_period ?? ""}${scope}`;
  }
  return `${thresholdText(r, sym, unit)}${scope}`;
};

const getRuleChipLabel = (item: NotificationSettingsItem): string => {
  const rule = item.rule;
  if (!rule) return "";
  const sym = OP_SYMBOL[rule.operator ?? ""] ?? "";

  if (item.system === "paid_media") {
    return getPaidMediaChipLabel(item, rule as PaidMediaRule, sym);
  }
  if (item.system === "social") {
    return getSocialChipLabel(item, rule as SocialRule, sym);
  }
  if (item.system === "sales") {
    return getSalesChipLabel(item, rule as SalesRule, sym);
  }
  return getWebAnalyticsChipLabel(item, rule as WebAnalyticsRule, sym);
};

type CitiesFetcher = (_search?: string) => Promise<CitiesResponse>;

const getCityFetcher = (system?: NotificationSystem): CitiesFetcher | null => {
  if (system === "web_analytics") return fetchWebAnalyticsCities;
  if (system === "sales") return fetchSalesCities;
  return null;
};

const getPeriodOptionsForItem = (
  system: NotificationSystem,
  item: NotificationSettingsItem,
  options: NotificationOptionsResponse | null
): NotificationOption[] => {
  if (!options) return [];
  return options.metric_periods?.[system]?.[item.key] ?? [];
};

const getComparisonOptionsForItem = (
  system: NotificationSystem,
  item: NotificationSettingsItem,
  options: NotificationOptionsResponse | null
): NotificationOption[] => {
  if (!options) return [];
  return options.metric_comparison_periods?.[system]?.[item.key] ?? [];
};

const usesComparison = (item: NotificationSettingsItem): boolean => {
  const periods = item.allowed_periods ?? [];
  const comparisons = item.allowed_comparison_periods ?? [];
  return periods.length === 0 && comparisons.length > 0;
};

type DropdownOption = { value: string; label: string };
type PageTarget = {
  platform: string;
  page_id: string;
  platform_label: string;
  page_name?: string;
  account_name?: string;
};

const isEditingExistingRule = (item: NotificationSettingsItem): boolean =>
  item.configured && item.rule !== null;

const hasConditionAndValueForKind = (kind: NotificationKind): boolean =>
  kind === "threshold" || kind === "city" || kind === "growth";

const pageKey = (platform: string, pageId: string) => `${platform}::${pageId}`;

const getPanelValidity = (
  section: NotificationSection,
  item: NotificationSettingsItem,
  form: ConfigureFormState
): boolean => {
  const showComparison = usesComparison(item);
  const hasConditionAndValue = hasConditionAndValueForKind(item.kind);
  const isBetween = form.operator === "between";

  const isPeriodValid = showComparison
    ? Boolean(form.comparison_period)
    : Boolean(form.period);
  const isConditionValid = !hasConditionAndValue || Boolean(form.operator);
  const allowNegativeThreshold = isNegativeThresholdAllowed(section.system, item.key);
  const isNonNegativeIfRequired = (value: string) =>
    allowNegativeThreshold || value.trim() === "" || Number.parseFloat(value) >= 0;

  const isValueValid =
    !hasConditionAndValue ||
    (form.threshold_value.trim() !== "" &&
      isNonNegativeIfRequired(form.threshold_value) &&
      (!isBetween ||
        (form.threshold_value_upper.trim() !== "" &&
          isNonNegativeIfRequired(form.threshold_value_upper) &&
          Number.parseFloat(form.threshold_value) !==
            Number.parseFloat(form.threshold_value_upper))));
  const isPlatformValid =
    section.system === "paid_media"
      ? form.platforms.length > 0
      : section.system !== "social" || form.social_platforms.length > 0;

  return isPeriodValid && isConditionValid && isValueValid && isPlatformValid;
};

const getSaveButtonLabel = (
  saving: boolean,
  isEdit: boolean,
  t: TFunction
): string => {
  if (saving) {
    return t("notificationPreferences.configureThreshold.saving", {
      defaultValue: "Saving…",
    });
  }
  if (isEdit) {
    return t("notificationPreferences.configureThreshold.update", {
      defaultValue: "Update",
    });
  }
  return t("notificationPreferences.configureThreshold.save", {
    defaultValue: "Save Threshold",
  });
};

const buildCampaignData = (
  selected: PaidMediaCampaign[],
  available: PaidMediaCampaign[]
): { map: Map<string, PaidMediaCampaign>; options: DropdownOption[] } => {
  const map = new Map<string, PaidMediaCampaign>();
  [...selected, ...available].forEach(c => {
    if (!map.has(c.campaign_id)) map.set(c.campaign_id, c);
  });
  const options = Array.from(map.values()).map(c => ({
    value: c.campaign_id,
    label: c.platform_label
      ? `${c.campaign_name} (${c.platform_label})`
      : c.campaign_name,
  }));
  return { map, options };
};

const buildPageData = (
  socialPlatforms: SocialPlatformData[],
  targets: PageTarget[],
  selectedPlatforms: string[]
): { map: Map<string, PageTarget>; options: DropdownOption[] } => {
  const map = new Map<string, PageTarget>();
  socialPlatforms
    .filter(sp => sp.available)
    .forEach(sp =>
      sp.pages.forEach(page =>
        map.set(pageKey(page.platform, page.page_id), {
          platform: page.platform,
          page_id: page.page_id,
          platform_label: sp.label,
          page_name: page.page_name,
          account_name: sp.account_name,
        })
      )
    );
  targets.forEach(tgt => {
    const k = pageKey(tgt.platform, tgt.page_id);
    if (!map.has(k)) map.set(k, tgt);
  });
  const options = Array.from(map.values())
    .filter(tgt => selectedPlatforms.includes(tgt.platform))
    .map(tgt => {
      const name = tgt.page_name || tgt.account_name;
      return {
        value: pageKey(tgt.platform, tgt.page_id),
        label: name
          ? `${name} - ${tgt.page_id} (${tgt.platform_label})`
          : `${tgt.page_id} (${tgt.platform_label})`,
      };
    });
  return { map, options };
};

const buildCityOptions = (
  cities: string[],
  available: string[]
): DropdownOption[] =>
  Array.from(new Set([...cities, ...available])).map(c => ({
    value: c,
    label: c,
  }));

const buildProductData = (
  selected: SalesProduct[],
  available: SalesProduct[]
): { map: Map<string, SalesProduct>; options: DropdownOption[] } => {
  const map = new Map<string, SalesProduct>();
  [...selected, ...available].forEach(p => {
    if (!map.has(p.product_id)) map.set(p.product_id, p);
  });
  const options = Array.from(map.values()).map(p => ({
    value: p.product_id,
    label: p.product_name,
  }));
  return { map, options };
};

const buildPaidMediaForm = (r: PaidMediaRule): ConfigureFormState => ({
  ...DEFAULT_FORM,
  platforms: r.platforms ?? [],
  operator: r.operator ?? "gt",
  threshold_value: r.threshold_value === null ? "" : String(r.threshold_value),
  threshold_value_upper:
    r.threshold_value_upper === null ? "" : String(r.threshold_value_upper),
  period: r.period ?? "daily",
  campaigns: r.campaigns ?? [],
  email: r.email,
  push: r.push,
  in_app: r.in_app,
});

const buildSocialForm = (r: SocialRule): ConfigureFormState => ({
  ...DEFAULT_FORM,
  operator: r.operator ?? "gt",
  threshold_type: r.threshold_type ?? "absolute",
  threshold_value: r.threshold_value === null ? "" : String(r.threshold_value),
  threshold_value_upper:
    r.threshold_value_upper === null ? "" : String(r.threshold_value_upper),
  period: r.period ?? "daily",
  social_platforms: Array.from(new Set((r.targets ?? []).map(tgt => tgt.platform))),
  targets: (r.targets ?? []).map(tgt => ({
    platform: tgt.platform,
    page_id: tgt.page_id,
    platform_label: tgt.platform_label,
    page_name: tgt.page_name,
  })),
  email: r.email,
  push: r.push,
  in_app: r.in_app,
});

const buildWebAnalyticsForm = (r: WebAnalyticsRule): ConfigureFormState => ({
  ...DEFAULT_FORM,
  operator: r.operator ?? "gt",
  threshold_value: r.threshold_value === null ? "" : String(r.threshold_value),
  threshold_value_upper:
    r.threshold_value_upper === null ? "" : String(r.threshold_value_upper),
  period: r.period ?? "current_month",
  cities: r.cities ?? [],
  email: r.email,
  push: r.push,
  in_app: r.in_app,
});

const buildSalesForm = (r: SalesRule): ConfigureFormState => ({
  ...DEFAULT_FORM,
  operator: r.operator ?? "gt",
  threshold_value: r.threshold_value === null ? "" : String(r.threshold_value),
  threshold_value_upper:
    r.threshold_value_upper === null ? "" : String(r.threshold_value_upper),
  period: r.period ?? "daily",
  comparison_period: r.comparison_period ?? "wow",
  cities: r.cities ?? [],
  products: r.products ?? [],
  email: r.email,
  push: r.push,
  in_app: r.in_app,
});

const buildConfigureForm = (
  system: NotificationSystem,
  rule: AnyRule | null,
  defaultPeriod: string,
  defaultComparison: string
): ConfigureFormState => {
  if (!rule)
    return {
      ...DEFAULT_FORM,
      period: defaultPeriod,
      comparison_period: defaultComparison || DEFAULT_FORM.comparison_period,
    };
  if (system === "paid_media") return buildPaidMediaForm(rule as PaidMediaRule);
  if (system === "social") return buildSocialForm(rule as SocialRule);
  if (system === "sales") return buildSalesForm(rule as SalesRule);
  return buildWebAnalyticsForm(rule as WebAnalyticsRule);
};

const updateRuleChannel = (
  sections: NotificationSection[],
  sectionIdx: number,
  itemIdx: number,
  channel: "email" | "push" | "in_app",
  checked: boolean
): NotificationSection[] =>
  sections.map((s, si) => {
    if (si !== sectionIdx) return s;
    return {
      ...s,
      items: s.items.map((it, ii) => {
        if (ii !== itemIdx || !it.rule) return it;
        return { ...it, rule: { ...it.rule, [channel]: checked } };
      }),
    };
  });

const buildPaidMediaPayload = (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    platforms: form.platforms,
    period: form.period,
    email: form.email,
    push: form.push,
    in_app: form.in_app,
    is_enabled: true,
  };
  if (!isEdit) payload.metric = item.key;
  if (item.kind === "threshold") {
    payload.operator = form.operator;
    payload.threshold_value = Number.parseFloat(form.threshold_value) || 0;
    payload.threshold_value_upper =
      form.operator === "between"
        ? Number.parseFloat(form.threshold_value_upper) || 0
        : null;
    payload.campaigns = form.campaigns;
  }
  return payload;
};

const savePaidMediaRule = async (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Promise<void> => {
  const payload = buildPaidMediaPayload(item, form, isEdit);
  if (isEdit) {
    await updatePaidMediaRule((item.rule as PaidMediaRule).rule_id, payload);
  } else {
    await createPaidMediaRule(payload);
  }
};

const buildSocialPayload = (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    period: form.period,
    targets: form.targets,
    email: form.email,
    push: form.push,
    in_app: form.in_app,
    is_enabled: true,
  };
  if (!isEdit) payload.rule_type = item.key;
  if (item.kind === "threshold") {
    payload.operator = form.operator;
    payload.threshold_value = Number.parseFloat(form.threshold_value) || 0;
    payload.threshold_value_upper =
      form.operator === "between"
        ? Number.parseFloat(form.threshold_value_upper) || 0
        : null;
    if (item.key === "follower_growth") payload.threshold_type = form.threshold_type;
  }
  return payload;
};

const saveSocialRule = async (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Promise<void> => {
  const payload = buildSocialPayload(item, form, isEdit);
  if (isEdit) {
    await updateSocialRule((item.rule as SocialRule).rule_id, payload);
  } else {
    await createSocialRule(payload);
  }
};

const buildWebAnalyticsPayload = (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    period: form.period,
    email: form.email,
    push: form.push,
    in_app: form.in_app,
    is_enabled: true,
  };
  if (!isEdit) payload.metric = item.key;
  if (item.kind === "city") {
    payload.operator = form.operator;
    payload.threshold_value = Number.parseFloat(form.threshold_value) || 0;
    payload.threshold_value_upper =
      form.operator === "between"
        ? Number.parseFloat(form.threshold_value_upper) || 0
        : null;
    payload.cities = form.cities;
  }
  return payload;
};

const saveWebAnalyticsRule = async (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Promise<void> => {
  const payload = buildWebAnalyticsPayload(item, form, isEdit);
  if (isEdit) {
    await updateWebAnalyticsRule((item.rule as WebAnalyticsRule).rule_id, payload);
  } else {
    await createWebAnalyticsRule(payload);
  }
};

const buildSalesPayload = (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    operator: form.operator,
    threshold_value: Number.parseFloat(form.threshold_value) || 0,
    threshold_value_upper:
      form.operator === "between"
        ? Number.parseFloat(form.threshold_value_upper) || 0
        : null,
    cities: form.cities,
    products: form.products,
    email: form.email,
    push: form.push,
    in_app: form.in_app,
    is_enabled: true,
  };
  if (!isEdit) payload.metric = item.key;

  if (usesComparison(item)) {
    payload.comparison_period = form.comparison_period;
  } else {
    payload.period = form.period;
  }
  return payload;
};

const saveSalesRule = async (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Promise<void> => {
  const payload = buildSalesPayload(item, form, isEdit);
  if (isEdit) {
    await updateSalesRule((item.rule as SalesRule).rule_id, payload);
  } else {
    await createSalesRule(payload);
  }
};

type NotificationChannel = "email" | "push" | "in_app";

interface NotifToggleCellProps {
  configured: boolean;
  checked: boolean;
  ariaLabel: string;
  // eslint-disable-next-line no-unused-vars
  onChange: (checked: boolean) => void;
}

const NotifToggleCell: React.FC<NotifToggleCellProps> = ({
  configured,
  checked,
  ariaLabel,
  onChange,
}) => (
  <td className="toggle-cell">
    <label
      className={`notif-toggle${configured ? "" : " notif-toggle--disabled"}`}
      aria-label={ariaLabel}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={!configured}
        onChange={e => onChange(e.target.checked)}
      />
      <span className="notif-toggle-slider" />
    </label>
  </td>
);

interface ActivityRowProps {
  item: NotificationSettingsItem;
  system: NotificationSystem;
  sectionIdx: number;
  itemIdx: number;
  isConfigOpen: boolean;
  deletingRuleId: number | null;
  t: TFunction;
  // eslint-disable-next-line no-unused-vars
  onToggle: (s: number, i: number, channel: NotificationChannel, checked: boolean) => void;
  // eslint-disable-next-line no-unused-vars
  onConfigure: (s: number, i: number) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete: (s: number, i: number) => void;
}

const ActivityRow: React.FC<ActivityRowProps> = ({
  item,
  system,
  sectionIdx,
  itemIdx,
  isConfigOpen,
  deletingRuleId,
  t,
  onToggle,
  onConfigure,
  onDelete,
}) => {
  const ruleId = item.rule ? item.rule.rule_id : null;
  const isDeleting = ruleId !== null && deletingRuleId === ruleId;
  const isChannelActive = Boolean(
    item.rule && (item.rule.email || item.rule.push || item.rule.in_app)
  );
  const metricHelp = getNotificationMetricHelp(t, system, item.key, item.label);

  return (
    <tr className={isConfigOpen ? "activity-row-active" : ""}>
      <td className="activity-details">
        <span className="activity-name-row">
          <span className="activity-name">{item.label}</span>
          {metricHelp ? (
            <DefinitionTooltip
              label={item.label}
              info={metricHelp}
              formulaTitle={t("notificationPreferences.formulaLabel", {
                defaultValue: "Formula",
              })}
            />
          ) : null}
        </span>
      </td>
      <NotifToggleCell
        configured={item.configured}
        checked={item.rule?.email ?? false}
        ariaLabel={t("notificationPreferences.table.emailToggle", {
          label: item.label,
          defaultValue: `Email notifications for ${item.label}`,
        })}
        onChange={checked => onToggle(sectionIdx, itemIdx, "email", checked)}
      />
      <NotifToggleCell
        configured={item.configured}
        checked={item.rule?.push ?? false}
        ariaLabel={t("notificationPreferences.table.pushToggle", {
          label: item.label,
          defaultValue: `Push notifications for ${item.label}`,
        })}
        onChange={checked => onToggle(sectionIdx, itemIdx, "push", checked)}
      />
      <NotifToggleCell
        configured={item.configured}
        checked={item.rule?.in_app ?? false}
        ariaLabel={t("notificationPreferences.table.inAppToggle", {
          label: item.label,
          defaultValue: `In-app notifications for ${item.label}`,
        })}
        onChange={checked => onToggle(sectionIdx, itemIdx, "in_app", checked)}
      />
      <td className="threshold-cell">
        {item.configured && item.rule ? (
          <div className="threshold-actions-cell">
            <button
              className={`threshold-badge${isChannelActive ? "" : " threshold-badge--muted"}`}
              onClick={() => onConfigure(sectionIdx, itemIdx)}
            >
              <span>{getRuleChipLabel(item)}</span>
              <EditOutlined className="threshold-edit-icon" />
            </button>
            <button
              className={`threshold-delete-btn${isChannelActive ? "" : " threshold-delete-btn--muted"}`}
              onClick={() => onDelete(sectionIdx, itemIdx)}
              disabled={isDeleting}
              title={t("notificationPreferences.table.deleteRule", {
                defaultValue: "Remove rule",
              })}
            >
              <DeleteOutlined />
            </button>
          </div>
        ) : (
          <button
            className={`configure-btn button button--outline${isConfigOpen ? " configure-btn--active" : ""}`}
            onClick={() => onConfigure(sectionIdx, itemIdx)}
          >
            {t("notificationPreferences.table.configure", {
              defaultValue: "Configure",
            })}
          </button>
        )}
      </td>
    </tr>
  );
};

const NotificationPreferences: React.FC = () => {
  const [sections, setSections] = useState<NotificationSection[]>([]);
  const [expanded, setExpanded] = useState<boolean[]>([]);
  const [options, setOptions] = useState<NotificationOptionsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [digestFrequency, setDigestFrequency] = useState<string>("daily");
  const [savingMaster, setSavingMaster] = useState(false);

  const channelMasters = useMemo<NotificationChannelMasters>(
    () => ({
      email: sections.some(section => section.items.some(item => item.rule?.email)),
      push: sections.some(section => section.items.some(item => item.rule?.push)),
      in_app: sections.some(section => section.items.some(item => item.rule?.in_app)),
    }),
    [sections]
  );

  const [configureTarget, setConfigureTarget] = useState<{
    sectionIdx: number;
    itemIdx: number;
  } | null>(null);
  const [form, setForm] = useState<ConfigureFormState>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingRuleId, setDeletingRuleId] = useState<number | null>(null);
  const configurePanelRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (configureTarget) {
      configurePanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [configureTarget]);

  // Picker data
  const [availablePlatforms, setAvailablePlatforms] = useState<{ key: string; label: string }[]>([]);
  const [availableCampaigns, setAvailableCampaigns] = useState<PaidMediaCampaign[]>([]);
  const [campaignSearch, setCampaignSearch] = useState<string>("");
  const [socialPlatforms, setSocialPlatforms] = useState<SocialPlatformData[]>([]);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [citySearch, setCitySearch] = useState<string>("");
  const [cityAvailable, setCityAvailable] = useState<boolean>(true);
  const [availableProducts, setAvailableProducts] = useState<SalesProduct[]>([]);
  const [productSearch, setProductSearch] = useState<string>("");
  const [productAvailable, setProductAvailable] = useState<boolean>(true);

  const { t, i18n } = useTranslation();
  useLanguage();
  const apiLang = getApiLangFromI18n(i18n);

  const fetchData = async (lang: "en" | "ar") => {
    try {
      setLoading(true);
      const [settingsRes, optionsRes] = await Promise.all([
        fetchNotificationSettings(lang),
        fetchNotificationOptions(lang),
      ]);
      const secs = settingsRes.notification_sections ?? [];
      setSections(secs);
      setExpanded(prev =>
        prev.length === secs.length ? prev : new Array(secs.length).fill(false)
      );
      setOptions(optionsRes);
      setDigestFrequency(settingsRes.notification_refresh_frequency ?? "daily");
    } catch (err) {
      console.error("Error fetching notification data:", err);
    } finally {
      setLoading(false);
    }
  };

  const refreshSettings = async () => {
    try {
      const settingsRes = await fetchNotificationSettings(apiLang);
      setSections(settingsRes.notification_sections ?? []);
    } catch (err) {
      console.error("Error refreshing notification settings:", err);
    }
  };

  useEffect(() => {
    fetchData(apiLang);
  }, [apiLang]);

  // Reload campaigns when platforms or search changes
  useEffect(() => {
    if (!configureTarget) return;
    const section = sections[configureTarget.sectionIdx];
    if (section?.system !== "paid_media" || form.platforms.length === 0) {
      setAvailableCampaigns([]);
      return;
    }
    fetchPaidMediaCampaigns(form.platforms, campaignSearch)
      .then(data => setAvailableCampaigns(data.campaigns ?? []))
      .catch(() => {});
  }, [form.platforms, campaignSearch, configureTarget]);

  // Reload cities when search changes (web analytics + sales use different endpoints)
  useEffect(() => {
    if (!configureTarget) return;
    const section = sections[configureTarget.sectionIdx];
    const cityFetcher = getCityFetcher(section?.system);
    if (!cityFetcher) return;
    cityFetcher(citySearch)
      .then((data: CitiesResponse) => {
        setAvailableCities(data.cities ?? []);
        if (!citySearch.trim()) setCityAvailable(data.available);
      })
      .catch(() => {});
  }, [citySearch, configureTarget]);

  useEffect(() => {
    if (!configureTarget) return;
    const section = sections[configureTarget.sectionIdx];
    if (section?.system !== "sales") return;
    fetchSalesProducts(productSearch)
      .then(data => {
        setAvailableProducts(data.products ?? []);
        if (!productSearch.trim()) setProductAvailable(data.available);
      })
      .catch(() => {});
  }, [productSearch, configureTarget]);

  const toggleSection = (idx: number) =>
    setExpanded(prev => prev.map((v, i) => (i === idx ? !v : v)));

  const fetchPickerData = (system: NotificationSystem) => {
    if (system === "paid_media") {
      fetchPaidMediaPlatforms()
        .then(data => setAvailablePlatforms(data.platforms ?? []))
        .catch(() => {});
    } else if (system === "social") {
      fetchSocialPlatforms()
        .then(data => setSocialPlatforms(data.platforms ?? []))
        .catch(() => {});
    } else if (system === "sales") {
      fetchSalesCities("")
        .then(data => {
          setAvailableCities(data.cities ?? []);
          setCityAvailable(data.available);
        })
        .catch(() => {});
      fetchSalesProducts("")
        .then(data => {
          setAvailableProducts(data.products ?? []);
          setProductAvailable(data.available);
        })
        .catch(() => {});
    } else {
      fetchWebAnalyticsCities("")
        .then(data => {
          setAvailableCities(data.cities ?? []);
          setCityAvailable(data.available);
        })
        .catch(() => {});
    }
  };

  const handleOpenConfigure = (sectionIdx: number, itemIdx: number) => {
    const section = sections[sectionIdx];
    const item = section.items[itemIdx];
    const defaultPeriod =
      getPeriodOptionsForItem(section.system, item, options)[0]?.value ??
      DEFAULT_FORM.period;
    const defaultComparison =
      getComparisonOptionsForItem(section.system, item, options)[0]?.value ??
      DEFAULT_FORM.comparison_period;

    setForm(
      buildConfigureForm(
        section.system,
        item.rule,
        defaultPeriod,
        defaultComparison
      )
    );
    setCampaignSearch("");
    setCitySearch("");
    setProductSearch("");
    setAvailableCampaigns([]);
    setAvailableCities([]);
    setAvailableProducts([]);
    setConfigureTarget({ sectionIdx, itemIdx });

    fetchPickerData(section.system);
  };

  const handleToggle = async (
    sectionIdx: number,
    itemIdx: number,
    channel: "email" | "push" | "in_app",
    checked: boolean
  ) => {
    const item = sections[sectionIdx].items[itemIdx];
    if (!item.rule) return;
    const ruleId = item.rule.rule_id;

    setSections(prev => updateRuleChannel(prev, sectionIdx, itemIdx, channel, checked));

    try {
      const payload = { [channel]: checked };
      if (sections[sectionIdx].system === "paid_media") {
        await updatePaidMediaRule(ruleId, payload);
      } else if (sections[sectionIdx].system === "social") {
        await updateSocialRule(ruleId, payload);
      } else if (sections[sectionIdx].system === "sales") {
        await updateSalesRule(ruleId, payload);
      } else {
        await updateWebAnalyticsRule(ruleId, payload);
      }
    } catch (err) {
      console.error("Error toggling notification channel:", err);
      message.error(
        getErrorDetail(
          err,
          t("notificationPreferences.errors.toggleFailed", {
            defaultValue: "Could not update the notification channel.",
          })
        )
      );
      await refreshSettings();
    }
  };

  const handleMasterToggleChange = async (
    channel: "email" | "push" | "in_app",
    checked: boolean
  ) => {
    setSavingMaster(true);
    try {
      const response = await updateNotificationChannelMasters({ [channel]: checked });
      if (response.changes[channel]?.action === "no_configured_rules") {
        message.warning(
          t("notificationPreferences.errors.noConfiguredRules", {
            defaultValue: "Set up at least one notification before enabling this channel.",
          })
        );
      }
      await refreshSettings();
    } catch (err) {
      console.error("Error updating channel master:", err);
      message.error(
        getErrorDetail(
          err,
          t("notificationPreferences.errors.masterToggleFailed", {
            defaultValue: "Could not update the channel setting.",
          })
        )
      );
    } finally {
      setSavingMaster(false);
    }
  };

  const handleDeleteRule = async (sectionIdx: number, itemIdx: number) => {
    const item = sections[sectionIdx].items[itemIdx];
    if (!item.rule) return;
    const ruleId = item.rule.rule_id;
    setDeletingRuleId(ruleId);
    try {
      if (sections[sectionIdx].system === "paid_media") {
        await deletePaidMediaRule(ruleId);
      } else if (sections[sectionIdx].system === "social") {
        await deleteSocialRule(ruleId);
      } else if (sections[sectionIdx].system === "sales") {
        await deleteSalesRule(ruleId);
      } else {
        await deleteWebAnalyticsRule(ruleId);
      }
      if (
        configureTarget?.sectionIdx === sectionIdx &&
        configureTarget?.itemIdx === itemIdx
      ) {
        setConfigureTarget(null);
      }
      await refreshSettings();
    } catch (err) {
      console.error("Error deleting rule:", err);
      message.error(
        getErrorDetail(
          err,
          t("notificationPreferences.errors.deleteFailed", {
            defaultValue: "Could not remove the rule.",
          })
        )
      );
    } finally {
      setDeletingRuleId(null);
    }
  };

  const handleSaveThreshold = async () => {
    if (!configureTarget) return;
    const { sectionIdx, itemIdx } = configureTarget;
    const section = sections[sectionIdx];
    const item = section.items[itemIdx];
    const isEdit = item.configured && item.rule !== null;
    setSaving(true);
    try {
      if (section.system === "paid_media") {
        await savePaidMediaRule(item, form, isEdit);
      } else if (section.system === "social") {
        await saveSocialRule(item, form, isEdit);
      } else if (section.system === "sales") {
        await saveSalesRule(item, form, isEdit);
      } else {
        await saveWebAnalyticsRule(item, form, isEdit);
      }
      await refreshSettings();
      setConfigureTarget(null);
    } catch (err) {
      console.error("Error saving rule:", err);
      message.error(
        getErrorDetail(
          err,
          t("notificationPreferences.errors.saveFailed", {
            defaultValue: "Could not save the rule.",
          })
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePlatformsChange = (val: unknown) => {
    const next = (val as string[]) ?? [];
    setForm(f => ({
      ...f,
      platforms: next,
      campaigns: f.campaigns.filter(c => next.includes(c.platform)),
    }));
  };

  const handleCampaignsChange = (val: unknown, allCampaigns: PaidMediaCampaign[]) => {
    const ids = (val as string[]) ?? [];
    const map = new Map(allCampaigns.map(c => [c.campaign_id, c] as const));
    setForm(f => ({
      ...f,
      campaigns: ids.map(id => map.get(id)).filter((c): c is PaidMediaCampaign => Boolean(c)),
    }));
  };

  const handleSocialPlatformsChange = (val: unknown) => {
    const plats = (val as string[]) ?? [];
    setForm(f => ({
      ...f,
      social_platforms: plats,
      targets: f.targets.filter(tgt => plats.includes(tgt.platform)),
    }));
  };

  const handleTargetsChange = (val: unknown, availablePages: PageTarget[]) => {
    const keys = (val as string[]) ?? [];
    const map = new Map<string, PageTarget>(
      availablePages.map(p => [`${p.platform}::${p.page_id}`, p])
    );
    setForm(f => ({
      ...f,
      targets: keys.map(k => map.get(k)).filter((tgt): tgt is PageTarget => Boolean(tgt)),
    }));
  };

  const handleCitiesChange = (val: unknown) => {
    setForm(f => ({ ...f, cities: (val as string[]) ?? [] }));
  };

  const handleProductsChange = (val: unknown, allProducts: SalesProduct[]) => {
    const ids = (val as string[]) ?? [];
    const map = new Map(allProducts.map(p => [p.product_id, p] as const));
    setForm(f => ({
      ...f,
      products: ids
        .map(id => map.get(id))
        .filter((p): p is SalesProduct => Boolean(p)),
    }));
  };

  const renderConfigurePanel = (
    section: NotificationSection,
    item: NotificationSettingsItem,
    sectionIdx: number,
    itemIdx: number
  ): React.ReactNode => {
    const isEdit = isEditingExistingRule(item);
    const periodOptions = getPeriodOptionsForItem(section.system, item, options);
    const comparisonOptions = getComparisonOptionsForItem(
      section.system,
      item,
      options
    );

    const showComparison = usesComparison(item);
    const hasConditionAndValue = hasConditionAndValueForKind(item.kind);
    const isBetween = form.operator === "between";
    const allowNegativeThreshold = isNegativeThresholdAllowed(section.system, item.key);

    const isRequiredFieldsValid = getPanelValidity(section, item, form);
    const saveButtonLabel = getSaveButtonLabel(saving, isEdit, t);
    const availablePlatformLabelByKey = new Map(availablePlatforms.map(p => [p.key, p.label]));
    const restrictedPlatformKeys = PAID_MEDIA_METRIC_PLATFORM_RESTRICTIONS[item.key];
    const platformOptions = restrictedPlatformKeys?.length
      ? restrictedPlatformKeys.map(key => ({
          value: key,
          label: availablePlatformLabelByKey.get(key) ?? PLATFORM_LABELS[key] ?? key,
        }))
      : availablePlatforms.map(p => ({
          value: p.key,
          label: p.label,
        }));

    const hasNoPlatformOptions = platformOptions.length === 0;
    const socialPlatformOptions = socialPlatforms.map(sp => ({
      value: sp.key,
      label: sp.label,
    }));

    const { map: campaignMap, options: campaignOptions } = buildCampaignData(
      form.campaigns,
      availableCampaigns
    );
    const { map: pageMap, options: pageOptions } = buildPageData(
      socialPlatforms,
      form.targets,
      form.social_platforms
    );
    const cityOptions = buildCityOptions(form.cities, availableCities);
    const { map: productMap, options: productOptions } = buildProductData(
      form.products,
      availableProducts
    );

    return (
      <tr
        className="configure-panel-row"
        key={`cfg-${sectionIdx}-${itemIdx}`}
        ref={configurePanelRef}
      >
        <td colSpan={5}>
          <div className="configure-threshold-panel">
            <div className="configure-panel-header">
              <h4 className="configure-panel-title">
                {isEdit
                  ? t("notificationPreferences.configureThreshold.edit", {
                      defaultValue: `Edit: ${item.label}`,
                    })
                  : t("notificationPreferences.configureThreshold.title", {
                      defaultValue: "Configure Threshold",
                    })}
              </h4>
              <button
                className="configure-panel-close"
                onClick={() => setConfigureTarget(null)}
                title="Close"
              >
                <CloseOutlined />
              </button>
            </div>
            <div className="threshold-form-grid">

              {/* PAID MEDIA — Platforms (multi-select dropdown) */}
              {section.system === "paid_media" && (
                <div className="threshold-field">
                  <label className="threshold-label">
                    {t("notificationPreferences.configureThreshold.platform", {
                      defaultValue: "Platform",
                    })}
                    <span className="required-asterisk">*</span>
                  </label>
                  {hasNoPlatformOptions ? (
                    <span className="threshold-field-empty-note">
                      {t("notificationPreferences.configureThreshold.noPlatformsAvailable", {
                        defaultValue:
                          "No connected platform supports this metric yet. Connect the required ad account to configure it.",
                      })}
                    </span>
                  ) : (
                    <StyledDropdown
                      mode="multiple"
                      value={form.platforms}
                      options={platformOptions}
                      placeholder={t(
                        "notificationPreferences.configureThreshold.selectPlatform",
                        { defaultValue: "Select platform(s)" }
                      )}
                      onChange={handlePlatformsChange}
                      maxTagCount="responsive"
                      className="filter-dropdown"
                      popupClassName="filter-dropdown-menu"
                    />
                  )}
                </div>
              )}

              {/* PAID MEDIA — Campaigns (multi-select, searchable, threshold only) */}
              {section.system === "paid_media" && item.kind === "threshold" && (
                <div className="threshold-field threshold-field--optional">
                  <label className="threshold-label">
                    {t("notificationPreferences.configureThreshold.campaigns", {
                      defaultValue: "Campaign",
                    })}
                  </label>
                  <StyledDropdown
                    mode="multiple"
                    showSearch
                    filterOption={false}
                    disabled={form.platforms.length === 0}
                    value={form.campaigns.map(c => c.campaign_id)}
                    options={campaignOptions}
                    placeholder={t(
                      "notificationPreferences.configureThreshold.searchCampaigns",
                      { defaultValue: "Search campaigns…" }
                    )}
                    onSearch={val => setCampaignSearch(val)}
                    onChange={val =>
                      handleCampaignsChange(val, Array.from(campaignMap.values()))
                    }
                    maxTagCount="responsive"
                    maxTagTextLength={16}
                    className="filter-dropdown"
                    popupClassName="filter-dropdown-menu"
                  />
                </div>
              )}

              {/* SOCIAL — Platforms (multi-select; drives the Pages dropdown) */}
              {section.system === "social" && (
                <div className="threshold-field">
                  <label className="threshold-label">
                    {t("notificationPreferences.configureThreshold.platform", {
                      defaultValue: "Platform",
                    })}
                    <span className="required-asterisk">*</span>
                  </label>
                  <StyledDropdown
                    mode="multiple"
                    value={form.social_platforms}
                    options={socialPlatformOptions}
                    placeholder={t(
                      "notificationPreferences.configureThreshold.selectPlatform",
                      { defaultValue: "Select platform(s)" }
                    )}
                    onChange={handleSocialPlatformsChange}
                    maxTagCount="responsive"
                    className="filter-dropdown"
                    popupClassName="filter-dropdown-menu"
                  />
                </div>
              )}

              {/* SOCIAL — Page targets (filtered by selected platform) */}
              {section.system === "social" && (
                <div className="threshold-field threshold-field--optional">
                  <label className="threshold-label">
                    {t("notificationPreferences.configureThreshold.pages", {
                      defaultValue: "Pages",
                    })}
                  </label>
                  <StyledDropdown
                    mode="multiple"
                    disabled={form.social_platforms.length === 0}
                    value={form.targets.map(tgt => pageKey(tgt.platform, tgt.page_id))}
                    options={pageOptions}
                    placeholder={t(
                      "notificationPreferences.configureThreshold.selectPages",
                      { defaultValue: "Select page(s)" }
                    )}
                    onChange={val =>
                      handleTargetsChange(val, Array.from(pageMap.values()))
                    }
                    maxTagCount="responsive"
                    className="filter-dropdown"
                    popupClassName="filter-dropdown-menu"
                  />
                </div>
              )}

              {/* WEB ANALYTICS — Cities (multi-select, searchable) */}
              {section.system === "web_analytics" && item.kind === "city" && (
                <div className="threshold-field threshold-field--optional">
                  <label className="threshold-label">
                    {t("notificationPreferences.configureThreshold.cities", {
                      defaultValue: "Cities",
                    })}
                  </label>
                  {!cityAvailable && form.cities.length === 0 && !citySearch.trim() ? (
                    <span className="unavailable-note">
                      {t(
                        "notificationPreferences.configureThreshold.citiesUnavailable",
                        { defaultValue: "City data not available" }
                      )}
                    </span>
                  ) : (
                    <StyledDropdown
                      mode="multiple"
                      showSearch
                      filterOption={false}
                      value={form.cities}
                      options={cityOptions}
                      placeholder={t(
                        "notificationPreferences.configureThreshold.searchCities",
                        { defaultValue: "Search cities…" }
                      )}
                      onSearch={val => setCitySearch(val)}
                      onChange={handleCitiesChange}
                      maxTagCount="responsive"
                      className="filter-dropdown"
                      popupClassName="filter-dropdown-menu"
                    />
                  )}
                </div>
              )}

              {/* SALES — Cities (multi-select, searchable, optional scope) */}
              {section.system === "sales" && (
                <div className="threshold-field threshold-field--optional">
                  <label className="threshold-label">
                    {t("notificationPreferences.configureThreshold.cities", {
                      defaultValue: "Cities",
                    })}
                  </label>
                  {!cityAvailable && form.cities.length === 0 && !citySearch.trim() ? (
                    <span className="unavailable-note">
                      {t(
                        "notificationPreferences.configureThreshold.citiesUnavailable",
                        { defaultValue: "City data not available" }
                      )}
                    </span>
                  ) : (
                    <StyledDropdown
                      mode="multiple"
                      showSearch
                      filterOption={false}
                      value={form.cities}
                      options={cityOptions}
                      placeholder={t(
                        "notificationPreferences.configureThreshold.searchCities",
                        { defaultValue: "Search cities…" }
                      )}
                      onSearch={val => setCitySearch(val)}
                      onChange={handleCitiesChange}
                      maxTagCount="responsive"
                      className="filter-dropdown"
                      popupClassName="filter-dropdown-menu"
                    />
                  )}
                </div>
              )}

              {/* SALES — Products (multi-select, searchable, optional scope) */}
              {section.system === "sales" && (
                <div className="threshold-field threshold-field--optional">
                  <label className="threshold-label">
                    {t("notificationPreferences.configureThreshold.products", {
                      defaultValue: "Products",
                    })}
                  </label>
                  {!productAvailable && form.products.length === 0 && !productSearch.trim() ? (
                    <span className="unavailable-note">
                      {t(
                        "notificationPreferences.configureThreshold.productsUnavailable",
                        { defaultValue: "Product data not available" }
                      )}
                    </span>
                  ) : (
                    <StyledDropdown
                      mode="multiple"
                      showSearch
                      filterOption={false}
                      value={form.products.map(p => p.product_id)}
                      options={productOptions}
                      placeholder={t(
                        "notificationPreferences.configureThreshold.searchProducts",
                        { defaultValue: "Search products…" }
                      )}
                      onSearch={val => setProductSearch(val)}
                      onChange={val =>
                        handleProductsChange(val, Array.from(productMap.values()))
                      }
                      maxTagCount="responsive"
                      maxTagTextLength={16}
                      className="filter-dropdown"
                      popupClassName="filter-dropdown-menu"
                    />
                  )}
                </div>
              )}


              {showComparison ? (
                <div className="threshold-field">
                  <label className="threshold-label">
                    {t("notificationPreferences.configureThreshold.comparison", {
                      defaultValue: "Comparison",
                    })}
                    <span className="required-asterisk">*</span>
                  </label>
                  <StyledDropdown
                    value={form.comparison_period}
                    onChange={val =>
                      setForm(f => ({ ...f, comparison_period: val as string }))
                    }
                    options={comparisonOptions}
                    className="filter-dropdown"
                    popupClassName="filter-dropdown-menu"
                  />
                </div>
              ) : (
                <div className="threshold-field">
                  <label className="threshold-label">
                    {t("notificationPreferences.configureThreshold.period", {
                      defaultValue: "Period",
                    })}
                    <span className="required-asterisk">*</span>
                  </label>
                  <StyledDropdown
                    value={form.period}
                    onChange={val => setForm(f => ({ ...f, period: val as string }))}
                    options={periodOptions}
                    className="filter-dropdown"
                    popupClassName="filter-dropdown-menu"
                  />
                </div>
              )}

              {/* Condition (operator) */}
              {hasConditionAndValue && (
                <div className="threshold-field">
                  <label className="threshold-label">
                    {t("notificationPreferences.configureThreshold.condition", {
                      defaultValue: "Condition",
                    })}
                    <span className="required-asterisk">*</span>
                  </label>
                  <StyledDropdown
                    value={form.operator}
                    onChange={val => setForm(f => ({ ...f, operator: val as string }))}
                    options={options?.operators ?? []}
                    className="filter-dropdown"
                    popupClassName="filter-dropdown-menu"
                  />
                </div>
              )}

              {/* Threshold type — follower_growth only */}
              {section.system === "social" && item.key === "follower_growth" && (
                <div className="threshold-field threshold-field--optional">
                  <label className="threshold-label">
                    {t(
                      "notificationPreferences.configureThreshold.thresholdType",
                      { defaultValue: "Threshold Type" }
                    )}
                  </label>
                  <StyledDropdown
                    value={form.threshold_type}
                    onChange={val =>
                      setForm(f => ({ ...f, threshold_type: val as string }))
                    }
                    options={options?.threshold_types ?? []}
                    className="filter-dropdown"
                    popupClassName="filter-dropdown-menu"
                  />
                </div>
              )}

              {/* Value (lower bound when operator is "between") */}
              {hasConditionAndValue && (
                <div className="threshold-field">
                  <label className="threshold-label">
                    {isBetween
                      ? t("notificationPreferences.configureThreshold.lowerValue", {
                          defaultValue: "Lower Value",
                        })
                      : t("notificationPreferences.configureThreshold.value", {
                          defaultValue: "Value",
                        })}
                    {item.unit === "percentage" && " (%)"}
                    {item.unit === "currency" && " (Currency)"}
                    {item.unit === "seconds" && " (Sec)"}
                    <span className="required-asterisk">*</span>
                    {item.unit === "currency" && (
                      <DefinitionTooltip
                        label={t("notificationPreferences.configureThreshold.value", {
                          defaultValue: "Value",
                        })}
                        info={{
                          description: t(
                            "notificationPreferences.configureThreshold.valueCurrencyTooltip",
                            {
                              defaultValue:
                                "Enter the threshold amount in your Shopify store's configured currency. The alert will trigger when the selected metric meets this value.",
                            }
                          ),
                        }}
                      />
                    )}
                  </label>
                  <Input
                    type="number"
                    min={allowNegativeThreshold ? undefined : 0}
                    value={form.threshold_value}
                    onChange={e => {
                      const next = allowNegativeThreshold
                        ? e.target.value
                        : e.target.value.replaceAll("-", "");
                      setForm(f => ({ ...f, threshold_value: next }));
                    }}
                    className="threshold-value-input"
                    placeholder="e.g. 1000"
                  />
                </div>
              )}

              {hasConditionAndValue && isBetween && (
                <div className="threshold-field">
                  <label className="threshold-label">
                    {t("notificationPreferences.configureThreshold.upperValue", {
                      defaultValue: "Upper Value",
                    })}
                    {item.unit === "percentage" && " (%)"}
                    {item.unit === "currency" && " (Currency)"}
                    {item.unit === "seconds" && " (Sec)"}
                    <span className="required-asterisk">*</span>
                  </label>
                  <Input
                    type="number"
                    min={allowNegativeThreshold ? undefined : 0}
                    value={form.threshold_value_upper}
                    onChange={e => {
                      const next = allowNegativeThreshold
                        ? e.target.value
                        : e.target.value.replaceAll("-", "");
                      setForm(f => ({ ...f, threshold_value_upper: next }));
                    }}
                    className="threshold-value-input"
                    placeholder="e.g. 5000"
                  />
                </div>
              )}

            </div>

            <div className="threshold-actions">
              <button
                className="threshold-cancel-btn button button--outline"
                onClick={() => setConfigureTarget(null)}
              >
                {t("notificationPreferences.configureThreshold.cancel", {
                  defaultValue: "Cancel",
                })}
              </button>
              <StyledTooltip
                content={
                  !saving && !isRequiredFieldsValid
                    ? t(
                        "notificationPreferences.configureThreshold.requiredFieldsTooltip",
                        { defaultValue: "Complete all required fields" }
                      )
                    : ""
                }
                placement="top"
              >
                <button
                  className="threshold-save-btn button button--Primary"
                  onClick={handleSaveThreshold}
                  disabled={saving || !isRequiredFieldsValid}
                >
                  {saveButtonLabel}
                </button>
              </StyledTooltip>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="preferences-loading">
        <div className="preferences-loading__spinner" />
      </div>
    );
  }

  const frequencyOptions = options?.frequencies ?? [
    { value: "daily", label: "Daily" },
    { value: "6h", label: "Every 6 Hours" },
  ];

  const digestDisplay =
    frequencyOptions.find(f => f.value === digestFrequency)?.label ?? digestFrequency;

  const digestNote = t("notificationPreferences.digestNote", {
    frequency: digestDisplay,
    defaultValue: `${digestDisplay} notifications will be sent at 10:00 AM for all your configured and enabled email or push alerts.`,
  });

  const hasNoConfiguredNotifications = sections.every(section =>
    section.items.every(item => !item.configured)
  );

  return (
    <div className="notification-preferences-container">
      <h1 className="notifications-page-title">
        {t("notificationPreferences.title")}
      </h1>
      <span className="notifications-page-description">
        {t("notificationPreferences.subtitle", {
          defaultValue:
            "Customize how you receive notifications and define when alerts should be triggered.",
        })}
      </span>
      <span className="notifications-page-hint">
        {t("notificationPreferences.hint", {
          defaultValue:
            "Turn on notification channels and configure thresholds only where you need custom alert rules.",
        })}
      </span>

      {/* Digest Frequency */}
      <div className="digest-frequency-card">
        <div className="digest-frequency-left">
          <div className="digest-frequency-info">
            <span className="digest-frequency-title">
              {t("notificationPreferences.digestFrequency.title", {
                defaultValue: "Notification Digest Frequency",
              })}
            </span>
            <span className="digest-frequency-subtitle">
              {t("notificationPreferences.digestFrequency.subtitle", {
                defaultValue: "Control how often you receive rolled-up alerts.",
              })}
            </span>
            <span className="digest-frequency-note">{digestNote}</span>
          </div>
        </div>

        <StyledDropdown
          value={digestFrequency}
          disabled
          aria-label={t("notificationPreferences.digestFrequency.valueAriaLabel", {
            defaultValue: "Current digest frequency (fixed)",
          })}
          className="digest-frequency-select"
          options={frequencyOptions}
        />
      </div>

      {/* Enable Notification — per-channel master toggles */}
      <div className="channel-masters-card">
        <div className="channel-masters-info">
          <span className="channel-masters-title">
            {t("notificationPreferences.channelMasters.title", {
              defaultValue: "Enable Notification",
            })}
          </span>
          <span className="channel-masters-subtitle">
            {t("notificationPreferences.channelMasters.description", {
              defaultValue:
                "Email, Push, and In-App toggle independently, turning one off disables it for every category below, including those currently enabled, without affecting the other two channels.",
            })}
          </span>
        </div>
        <div
          className={`channel-masters-toggles${
            hasNoConfiguredNotifications ? " channel-masters-toggles--disabled" : ""
          }`}
        >
          {(["email", "push", "in_app"] as const).map(channel => (
            <div className="channel-master-toggle" key={channel}>
              <span className="channel-master-toggle__label">
                {t(`notificationPreferences.table.${CHANNEL_TABLE_LABEL_KEY[channel]}`, {
                  defaultValue:
                    channel === "email" ? "Email" : channel === "push" ? "Push" : "In-App",
                })}
              </span>
              <label
                className={`notif-toggle${
                  hasNoConfiguredNotifications ? " notif-toggle--disabled" : ""
                }`}
                aria-label={t("notificationPreferences.channelMasters.toggleAriaLabel", {
                  channel,
                  defaultValue: `Toggle ${channel} notifications for every category`,
                })}
              >
                <input
                  type="checkbox"
                  checked={channelMasters[channel]}
                  disabled={hasNoConfiguredNotifications || savingMaster}
                  onChange={e => handleMasterToggleChange(channel, e.target.checked)}
                />
                <span className="notif-toggle-slider" />
              </label>
            </div>
          ))}
          {hasNoConfiguredNotifications ? (
            <div className="channel-masters-disabled-tooltip" role="tooltip">
              {t("notificationPreferences.channelMasters.disabledTooltip", {
                defaultValue: "Set up at least one notification below before enabling a channel.",
              })}
            </div>
          ) : null}
        </div>
      </div>

      {/* Sections */}
      {sections.map((section, sectionIdx) => (
        <div key={section.system} className="notification-category-card">
          <button
            type="button"
            className="notification-category-header"
            aria-expanded={expanded[sectionIdx]}
            onClick={() => toggleSection(sectionIdx)}
          >
            <img
              src={SECTION_ICONS[section.system] ?? FALLBACK_SECTION_ICON}
              alt={section.category}
              className="category-icon"
            />
            <div className="category-info">
              <span className="category-title">
                {t(
                  `notificationPreferences.categories.${section.category}.label`,
                  { defaultValue: section.category }
                )}
              </span>
              <span className="category-description">{section.description}</span>
            </div>
            <span className="toggle-icon">
              {expanded[sectionIdx] ? <UpOutlined /> : <DownOutlined />}
            </span>
          </button>

          {expanded[sectionIdx] && (
            <div className="notification-table-wrapper">
              <table className="notification-table">
                <thead>
                  <tr className="table-main-row">
                    <th className="activity-col">
                      {t("notificationPreferences.table.activity", {
                        defaultValue: "Activity",
                      })}
                    </th>
                    <th className="toggle-col">
                      {t("notificationPreferences.table.email", {
                        defaultValue: "Email",
                      })}
                    </th>
                    <th className="toggle-col">
                      {t("notificationPreferences.table.push", {
                        defaultValue: "Push",
                      })}
                    </th>
                    <th className="toggle-col">
                      {t("notificationPreferences.table.inApp", {
                        defaultValue: "In-App",
                      })}
                    </th>
                    <th className="threshold-col">
                      {t("notificationPreferences.table.thresholdConfig", {
                        defaultValue: "Threshold Configuration",
                      })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {section.items.map((item, itemIdx) => {
                    const isConfigOpen =
                      configureTarget?.sectionIdx === sectionIdx &&
                      configureTarget?.itemIdx === itemIdx;

                    return (
                      <React.Fragment key={item.key}>
                        <ActivityRow
                          item={item}
                          system={section.system}
                          sectionIdx={sectionIdx}
                          itemIdx={itemIdx}
                          isConfigOpen={isConfigOpen}
                          deletingRuleId={deletingRuleId}
                          t={t}
                          onToggle={handleToggle}
                          onConfigure={handleOpenConfigure}
                          onDelete={handleDeleteRule}
                        />
                        {isConfigOpen &&
                          renderConfigurePanel(
                            section,
                            item,
                            sectionIdx,
                            itemIdx
                          )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default NotificationPreferences;
